import Navbar from "components/Navbar";
import PageLayout from "components/PageLayout";
import { routeConstants } from "constants/routeConstants";
import AgencyInformation from "pages/AgencyInformation";
import CustomSidebar from "pages/CustomSidebar";
import Dashboard from "pages/Dashboard";
import Details from "pages/Details";
import FindTalent from "pages/FindTalent";
import { ModelProfile } from "pages/FindTalent/ModelProfile/ModelProfile";
import FindWork from "pages/FindWork";
import WorkDetails from "pages/FindWork/WorkDetails";
import ForgotPassword from "pages/ForgotPassword";
import HelpCenter from "pages/HelpCenter";
import JobPost from "pages/Job/JobPost";
import JobAdvanceDetails from "pages/Job/JobPost/JobAdvanceDetails";
import JobBudget from "pages/Job/JobPost/JobBudget";
import JobDetails from "pages/Job/JobPost/JobDetails";
import JobLocation from "pages/Job/JobPost/JobLocation";
import JobPurpose from "pages/Job/JobPost/JobPurpose";
import MyJobListings from "pages/Job/MyJobListings/MyJobListings";
import Login from "pages/Login";
import Pricing from "pages/Pricing";
import Success from "pages/Pricing/Success";
import Profile from "pages/Profile";
import CompanyDetails from "pages/Profile/EditProfile/Agency/CompanyDetails";
import ContactInfo from "pages/Profile/EditProfile/Agency/ContactInfo";
import SocialMedia from "pages/Profile/EditProfile/Agency/SocialMedia";
import PhotosAndVideos from "pages/Profile/EditProfile/PhotosAndVideos";
import ResetPassword from "pages/ResetPassword";
import Saved from "pages/Saved";
import Settings from "pages/Settings";
import SignUp from "pages/SignUp";
import { lazy, Suspense, useCallback, useEffect, useMemo } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "slice";
import { logoutUser } from "slice/userAuth";
import ContactDetails from "../pages/Profile/EditProfile/ContactDetails";
import ExperienceDetails from "../pages/Profile/EditProfile/ExperienceDetails";
import LocationAndLanguages from "../pages/Profile/EditProfile/LocationAndLanguages";
import OtherInterests from "../pages/Profile/EditProfile/OtherInterests";
import PersonalDetails from "../pages/Profile/EditProfile/PersonalDetails";
import PhysicalDetails from "../pages/Profile/EditProfile/PhysicalDetails";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";

// const Login = lazy(() => import("../pages/Login")),
//     ForgotPassword = lazy(() => import("../pages/ForgotPassword")),
//     ResetPassword = lazy(() => import("../pages/ResetPassword")),
//     SignUp = lazy(() => import("../pages/SignUp")),
//     Dashboard = lazy(() => import("../pages/Dashboard")),
//     Details = lazy(() => import("../pages/Details")),
//     Profile = lazy(() => import("../pages/Profile")),
//     FindWork = lazy(() => import("../pages/FindWork")),
//     WorkDetails = lazy(() => import("../pages/FindWork/WorkDetails")),
//     JobPost = lazy(() => import("../pages/Job/JobPost")),
//     JobPurpose = lazy(() => import("../pages/Job/JobPost/JobPurpose")),
//     JobLocation = lazy(() => import("../pages/Job/JobPost/JobLocation")),
//     JobDetails = lazy(() => import("../pages/Job/JobPost/JobDetails")),
//     JobBudget = lazy(() => import("../pages/Job/JobPost/JobBudget")),
//     Success = lazy(() => import("../pages/Job/JobPost/Success")),
//     Pricing = lazy(() => import("../pages/Pricing")),
//     HelpCenter = lazy(() => import("../pages/HelpCenter")),
//     HelpCenterDetails = lazy(() => import("../pages/HelpCenter/Details")),
//     Settings = lazy(() => import("../pages/Settings")),
//     NotificationSettings = lazy(
//         () => import("../pages/Settings/Notifications")
//     ),
//     SubscriptionSettings = lazy(
//         () => import("../pages/Settings/Subscriptions")
//     ),
//     Saved = lazy(() => import("../pages/Saved")),
//     FindTalent = lazy(() => import("../pages/FindTalent")),
//     CompanyDetails = lazy(
//         () => import("../pages/Profile/EditProfile/Agency/CompanyDetails")
//     ),
//     ContactInfo = lazy(
//         () => import("../pages/Profile/EditProfile/Agency/ContactInfo")
//     ),
//     SocialMedia = lazy(
//         () => import("../pages/Profile/EditProfile/Agency/SocialMedia")
//     ),
//     AgencyInformation = lazy(() => import("../pages/AgencyInformation")),
//     MyJobListings = lazy(() => import("../pages/Job/MyJobListings"))

const PurchaseSuccess = lazy(() => import("../pages/Pricing/Success")),
    PurchaseCancel = lazy(() => import("../pages/Pricing/Cancel")),
    HelpCenterDetails = lazy(() => import("../pages/HelpCenter/Details")),
    NotificationSettings = lazy(
        () => import("../pages/Settings/Notifications")
    ),
    SubscriptionSettings = lazy(
        () => import("../pages/Settings/Subscriptions")
    ),
    MyPlanSettings = lazy(() => import("../pages/Pricing"));

const pathNames = [
    "/profile/edit",
    "/settings",
    "/pricing",
    "/find-work",
    "/jobs/post-a-job/",
    "/jobs/edit",
    "/jobs/listings",
];

const AppRoutes = () => {
    const user = useAppSelector((state) => state.user);
    const { pathname } = useLocation();
    const dispatch = useAppDispatch();

    const handleStorage = useCallback(() => {
        const userAuthFromStorage = localStorage.getItem("userAuth")
            ? JSON.parse(localStorage.getItem("userAuth") as string)
            : null;
        const userInfoFromStorage = localStorage.getItem("userInfo")
            ? JSON.parse(localStorage.getItem("userInfo") as string)
            : null;

        if (
            (userAuthFromStorage === null || userInfoFromStorage === null) &&
            !userAuthFromStorage.fromRefreshToken
        ) {
            dispatch(logoutUser());
        }
    }, [dispatch]);

    useEffect(() => {
        window.addEventListener("storage", handleStorage);

        return window.removeEventListener("storage", handleStorage);
    }, [handleStorage]);

    const isFullscreenLayout = useCallback(
        () => pathNames.some((path) => pathname.includes(path)),
        [pathname]
    );

    const authNavProps = useMemo(() => {
        if (
            ["/login", "/sign-up", "/forgot-password", "/reset-password"].some(
                (path) => pathname.includes(path)
            )
        ) {
            return {
                backgroundColor: "transparent",
                showBoxShadow: false,
            };
        }
    }, [pathname]);

    return (
        <>
            <Navbar {...authNavProps} />
            <PageLayout isFullScreenLayout={isFullscreenLayout()}>
                <CustomSidebar hidden={!isFullscreenLayout()} />
                <Suspense fallback={<div>Loading...</div>}>
                    <Routes>
                        <Route
                            path={routeConstants.root}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="/" element={<Dashboard />} />
                            <Route path="profile" element={<Profile />} />
                            <Route
                                path="profile/:userId"
                                element={<Profile />}
                            />
                        </Route>
                        <Route
                            path="/"
                            element={<PublicRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="login" element={<Login />} />
                            <Route
                                path="forgot-password"
                                element={<ForgotPassword />}
                            />
                            <Route
                                path="reset-password"
                                element={<ResetPassword />}
                            />
                            <Route path="sign-up" element={<SignUp />} />
                            <Route path="details" element={<Details />} />
                        </Route>
                        <Route
                            path={routeConstants.profile.editProfile.root}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route
                                path={
                                    routeConstants.profile.editProfile
                                        .personalDetails
                                }
                                element={<PersonalDetails />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile
                                        .photosAndVideos
                                }
                                element={<PhotosAndVideos />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile
                                        .physicalDetails
                                }
                                element={<PhysicalDetails />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile
                                        .experienceDetails
                                }
                                element={<ExperienceDetails />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile
                                        .otherInterests
                                }
                                element={<OtherInterests />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile
                                        .locationAndLanguages
                                }
                                element={<LocationAndLanguages />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile
                                        .contactDetails
                                }
                                element={<ContactDetails />}
                            />
                        </Route>
                        <Route
                            path={
                                routeConstants.profile.editProfile.agency.root
                            }
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route
                                path={
                                    routeConstants.profile.editProfile.agency
                                        .companyDetails
                                }
                                element={<CompanyDetails />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile.agency
                                        .contactInformation
                                }
                                element={<ContactInfo />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile.agency
                                        .socialMedia
                                }
                                element={<SocialMedia />}
                            />
                        </Route>
                        <Route
                            path={routeConstants.jobs.listings.root}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route
                                path={routeConstants.jobs.listings.active}
                                element={<MyJobListings type="all" />}
                            />
                            <Route
                                path={routeConstants.jobs.listings.active}
                                element={<MyJobListings type="active" />}
                            />
                            <Route
                                path={routeConstants.jobs.listings.inActive}
                                element={<MyJobListings type="inactive" />}
                            />
                            <Route
                                path={routeConstants.jobs.listings.expired}
                                element={<MyJobListings type="expired" />}
                            />
                            <Route
                                path={routeConstants.jobs.listings.drafts}
                                element={<MyJobListings type="draft" />}
                            />
                        </Route>
                        <Route
                            path={
                                routeConstants.profile.editProfile.agency.root
                            }
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route
                                path={
                                    routeConstants.profile.editProfile.agency
                                        .companyDetails
                                }
                                element={<CompanyDetails />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile.agency
                                        .contactInformation
                                }
                                element={<ContactInfo />}
                            />
                            <Route
                                path={
                                    routeConstants.profile.editProfile.agency
                                        .socialMedia
                                }
                                element={<SocialMedia />}
                            />
                        </Route>
                        <Route
                            path={routeConstants.jobs.listings.root}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route
                                path=""
                                element={<MyJobListings type="all" />}
                            />
                        </Route>
                        <Route
                            path={routeConstants.jobs.postAJob.root}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<JobPost />} />
                            <Route
                                path={routeConstants.jobs.postAJob.category}
                                element={<JobPost />}
                            />
                            <Route
                                path={routeConstants.jobs.postAJob.purpose}
                                element={<JobPurpose />}
                            />
                            <Route
                                path={routeConstants.jobs.postAJob.location}
                                element={<JobLocation />}
                            />
                            <Route
                                path={routeConstants.jobs.postAJob.details}
                                element={<JobDetails />}
                            />{" "}
                            <Route
                                path={
                                    routeConstants.jobs.postAJob.advanceDetails
                                }
                                element={<JobAdvanceDetails />}
                            />
                            <Route
                                path={routeConstants.jobs.postAJob.budget}
                                element={<JobBudget />}
                            />
                            <Route
                                path={routeConstants.jobs.postAJob.success}
                                element={<Success />}
                            />
                        </Route>
                        <Route
                            path={routeConstants.jobs.editAJob.root + ":id"}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<JobPost />} />
                            <Route
                                path={
                                    routeConstants.jobs.editAJob.root +
                                    ":id/" +
                                    routeConstants.jobs.editAJob.category
                                }
                                element={<JobPost />}
                            />
                            <Route
                                path={
                                    routeConstants.jobs.editAJob.root +
                                    ":id/" +
                                    routeConstants.jobs.editAJob.purpose
                                }
                                element={<JobPurpose />}
                            />
                            <Route
                                path={
                                    routeConstants.jobs.editAJob.root +
                                    ":id/" +
                                    routeConstants.jobs.editAJob.location
                                }
                                element={<JobLocation />}
                            />
                            <Route
                                path={
                                    routeConstants.jobs.editAJob.root +
                                    ":id/" +
                                    routeConstants.jobs.editAJob.details
                                }
                                element={<JobDetails />}
                            />
                            <Route
                                path={
                                    routeConstants.jobs.editAJob.root +
                                    ":id/" +
                                    routeConstants.jobs.editAJob.advanceDetails
                                }
                                element={<JobAdvanceDetails />}
                            />
                            <Route
                                path={
                                    routeConstants.jobs.editAJob.root +
                                    ":id/" +
                                    routeConstants.jobs.editAJob.budget
                                }
                                element={<JobBudget />}
                            />
                            <Route
                                path={
                                    routeConstants.jobs.editAJob.root +
                                    ":id/" +
                                    routeConstants.jobs.editAJob.success
                                }
                                element={<Success />}
                            />
                        </Route>
                        <Route
                            path="/"
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route
                                path="/find-talent"
                                element={<FindTalent />}
                            />
                            <Route
                                path={
                                    routeConstants.findTalentRoot.root +
                                    routeConstants.findTalentRoot.model
                                }
                                element={
                                    <PrivateRoute isLoggedIn={!!user.id} />
                                }
                            >
                                <Route path="" element={<ModelProfile />} />
                            </Route>
                        </Route>

                        <Route
                            path={routeConstants.pricing}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<Pricing />} />
                            <Route path=":planId" element={<Pricing />} />
                        </Route>
                        <Route
                            path={routeConstants.purchase.success}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<PurchaseSuccess />} />
                        </Route>
                        <Route
                            path={routeConstants.purchase.cancel}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<PurchaseCancel />} />
                        </Route>
                        <Route
                            path={routeConstants.helpCenter.root}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<HelpCenter />} />
                            <Route
                                path={routeConstants.helpCenter.details}
                                element={<HelpCenterDetails />}
                            />
                        </Route>
                        <Route
                            path={routeConstants.findWork.root}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<FindWork type="all" />} />
                            <Route
                                path={routeConstants.findWork.details}
                                element={<WorkDetails />}
                            />
                            <Route
                                path={routeConstants.findWork.allJobs}
                                element={<FindWork type="all" />}
                            />
                            <Route
                                path={routeConstants.findWork.likedJobs}
                                element={<FindWork type="liked" />}
                            />
                            <Route
                                path={routeConstants.findWork.appliedJobs}
                                element={<FindWork type="applied" />}
                            />
                            <Route
                                path={routeConstants.findWork.invitation}
                                element={<FindWork type="invited" />}
                            />
                        </Route>
                        <Route
                            path={routeConstants.settings.root}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route
                                path={routeConstants.settings.accounts}
                                element={<Settings />}
                            />
                            <Route
                                path={routeConstants.settings.notifications}
                                element={<NotificationSettings />}
                            />
                            <Route
                                path={routeConstants.settings.subscriptions}
                                element={<SubscriptionSettings />}
                            />
                            <Route
                                path={routeConstants.settings.plans}
                                element={<MyPlanSettings />}
                            />
                        </Route>
                        <Route
                            path="/saved"
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<Saved />} />
                        </Route>
                        <Route
                            path={routeConstants.agencyInformation}
                            element={<PrivateRoute isLoggedIn={!!user.id} />}
                        >
                            <Route path="" element={<AgencyInformation />} />
                            <Route path=":id" element={<AgencyInformation />} />
                        </Route>
                    </Routes>
                </Suspense>
            </PageLayout>
        </>
    );
};

export default AppRoutes;
